import React, { useState, useEffect } from 'react';
import type { PositionRule, AccountHolding, RuleTemplate } from '../../types';
import LoadingSpinner from './LoadingSpinner';

interface RuleCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rule: Partial<PositionRule>) => Promise<void>;
  symbol: string;
  exchange: string;
  accounts: AccountHolding[];
  templates: RuleTemplate[];
  companyName?: string;
}

interface FormData {
  rule_name: string;
  rule_scope: 'all_holdings' | 'specific_account';
  target_account_id?: number;
  action_type: 'SELL' | 'ALERT';
  trigger_type: 'TRAILING_STOP_LOSS' | 'FIXED_STOP_LOSS';
  trigger_value: number;
  sell_quantity_type?: 'FIXED_AMOUNT' | 'PERCENTAGE' | 'ALL_REMAINING';
  sell_quantity_value?: number;
  order_type?: 'MARKET' | 'LIMIT';
  limit_price?: number;
}

const RuleCreationModal: React.FC<RuleCreationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  symbol,
  exchange,
  accounts,
  templates,
  companyName
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    rule_name: '',
    rule_scope: 'all_holdings',
    action_type: 'SELL',
    trigger_type: 'TRAILING_STOP_LOSS',
    trigger_value: 10,
    sell_quantity_type: 'ALL_REMAINING',
    order_type: 'MARKET'
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFormData({
        rule_name: '',
        rule_scope: 'all_holdings',
        action_type: 'SELL',
        trigger_type: 'TRAILING_STOP_LOSS',
        trigger_value: 10,
        sell_quantity_type: 'ALL_REMAINING',
        order_type: 'MARKET'
      });
    }
  }, [isOpen]);

  const applyTemplate = (template: RuleTemplate) => {
    setFormData(prev => ({
      ...prev,
      rule_name: template.name,
      action_type: template.action_type,
      trigger_type: template.trigger_type,
      trigger_value: template.trigger_value,
      sell_quantity_type: template.sell_quantity_type,
      sell_quantity_value: template.sell_quantity_value,
      order_type: template.order_type
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit({
        ...formData,
        symbol,
        exchange,
        is_active: true,
        priority: 1
      });
      onClose();
    } catch (error) {
      console.error('Error creating rule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.rule_name.trim() !== '';
      case 2:
        return formData.rule_scope === 'all_holdings' || 
               (formData.rule_scope === 'specific_account' && formData.target_account_id);
      case 3:
        return formData.trigger_value > 0;
      case 4:
        return formData.action_type === 'ALERT' || 
               (formData.action_type === 'SELL' && 
                (formData.sell_quantity_type === 'ALL_REMAINING' || 
                 (formData.sell_quantity_value && formData.sell_quantity_value > 0)));
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Create Position Rule
                </h3>
                <p className="text-sm text-gray-500">
                  {symbol} ({exchange}) {companyName && `- ${companyName}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center justify-center mb-6">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === currentStep 
                      ? 'bg-blue-600 text-white' 
                      : step < currentStep 
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step < currentStep ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      step
                    )}
                  </div>
                  {step < 4 && (
                    <div className={`w-12 h-1 mx-2 ${step < currentStep ? 'bg-green-600' : 'bg-gray-200'}`}></div>
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Rule Template & Name */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">Rule Template & Name</h4>
                
                {/* Templates */}
                {templates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quick Templates
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => applyTemplate(template)}
                          className="text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{template.name}</div>
                          <div className="text-sm text-gray-500">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rule Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rule Name
                  </label>
                  <input
                    type="text"
                    value={formData.rule_name}
                    onChange={(e) => setFormData(prev => ({...prev, rule_name: e.target.value}))}
                    placeholder="e.g., Stop Loss at 10%"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Account Scope */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">Account Scope</h4>
                
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="radio"
                      value="all_holdings"
                      checked={formData.rule_scope === 'all_holdings'}
                      onChange={(e) => setFormData(prev => ({...prev, rule_scope: e.target.value as 'all_holdings'}))}
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">All Holdings</div>
                      <div className="text-sm text-gray-500">Apply rule to all accounts holding {symbol}</div>
                    </div>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="radio"
                      value="specific_account"
                      checked={formData.rule_scope === 'specific_account'}
                      onChange={(e) => setFormData(prev => ({...prev, rule_scope: e.target.value as 'specific_account'}))}
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Specific Account</div>
                      <div className="text-sm text-gray-500">Apply rule to a specific broker account</div>
                    </div>
                  </label>

                  {formData.rule_scope === 'specific_account' && (
                    <div className="ml-7">
                      <select
                        value={formData.target_account_id || ''}
                        onChange={(e) => setFormData(prev => ({...prev, target_account_id: parseInt(e.target.value)}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Account</option>
                        {accounts.map((account) => (
                          <option key={account.broker_account_id} value={account.broker_account_id}>
                            {account.broker_name} ({account.broker_type}) - {account.quantity} shares
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Trigger Conditions */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">Trigger Conditions</h4>

                {/* Action Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* SELL action temporarily disabled - broker integration required */}
                    {/* <label className="flex items-center">
                      <input
                        type="radio"
                        value="SELL"
                        checked={formData.action_type === 'SELL'}
                        onChange={(e) => setFormData(prev => ({...prev, action_type: e.target.value as 'SELL'}))}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">Sell Order</span>
                    </label> */}
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="ALERT"
                        checked={formData.action_type === 'ALERT'}
                        onChange={(e) => setFormData(prev => ({...prev, action_type: e.target.value as 'ALERT'}))}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">Alert Only</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Sell orders are temporarily disabled until broker integration is ready
                  </p>
                </div>

                {/* Trigger Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="TRAILING_STOP_LOSS"
                        checked={formData.trigger_type === 'TRAILING_STOP_LOSS'}
                        onChange={(e) => setFormData(prev => ({...prev, trigger_type: e.target.value as 'TRAILING_STOP_LOSS'}))}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">Trailing Stop Loss (%)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="FIXED_STOP_LOSS"
                        checked={formData.trigger_type === 'FIXED_STOP_LOSS'}
                        onChange={(e) => setFormData(prev => ({...prev, trigger_type: e.target.value as 'FIXED_STOP_LOSS'}))}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">Fixed Stop Loss (₹)</span>
                    </label>
                  </div>
                </div>

                {/* Trigger Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Value
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={formData.trigger_value}
                      onChange={(e) => setFormData(prev => ({...prev, trigger_value: parseFloat(e.target.value) || 0}))}
                      step="0.01"
                      min="0"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-500">
                      {formData.trigger_type === 'TRAILING_STOP_LOSS' ? '%' : '₹'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Sell Order Details */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900">
                  {formData.action_type === 'SELL' ? 'Sell Order Details' : 'Rule Summary'}
                </h4>

                {formData.action_type === 'SELL' && (
                  <>
                    {/* Quantity Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity to Sell
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="ALL_REMAINING"
                            checked={formData.sell_quantity_type === 'ALL_REMAINING'}
                            onChange={(e) => setFormData(prev => ({...prev, sell_quantity_type: e.target.value as 'ALL_REMAINING'}))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-900">All Remaining</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="PERCENTAGE"
                            checked={formData.sell_quantity_type === 'PERCENTAGE'}
                            onChange={(e) => setFormData(prev => ({...prev, sell_quantity_type: e.target.value as 'PERCENTAGE'}))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-900">Percentage</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="FIXED_AMOUNT"
                            checked={formData.sell_quantity_type === 'FIXED_AMOUNT'}
                            onChange={(e) => setFormData(prev => ({...prev, sell_quantity_type: e.target.value as 'FIXED_AMOUNT'}))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-900">Fixed Amount</span>
                        </label>
                      </div>

                      {(formData.sell_quantity_type === 'PERCENTAGE' || formData.sell_quantity_type === 'FIXED_AMOUNT') && (
                        <div className="mt-2">
                          <input
                            type="number"
                            value={formData.sell_quantity_value || ''}
                            onChange={(e) => setFormData(prev => ({...prev, sell_quantity_value: parseFloat(e.target.value) || 0}))}
                            step={formData.sell_quantity_type === 'PERCENTAGE' ? '1' : '0.01'}
                            min="0"
                            placeholder={formData.sell_quantity_type === 'PERCENTAGE' ? 'Enter percentage' : 'Enter quantity'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Order Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Order Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="MARKET"
                            checked={formData.order_type === 'MARKET'}
                            onChange={(e) => setFormData(prev => ({...prev, order_type: e.target.value as 'MARKET'}))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-900">Market Order</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="LIMIT"
                            checked={formData.order_type === 'LIMIT'}
                            onChange={(e) => setFormData(prev => ({...prev, order_type: e.target.value as 'LIMIT'}))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-900">Limit Order</span>
                        </label>
                      </div>

                      {formData.order_type === 'LIMIT' && (
                        <div className="mt-2">
                          <input
                            type="number"
                            value={formData.limit_price || ''}
                            onChange={(e) => setFormData(prev => ({...prev, limit_price: parseFloat(e.target.value) || 0}))}
                            step="0.01"
                            min="0"
                            placeholder="Enter limit price"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Rule Preview */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">Rule Preview</h5>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div><strong>Rule:</strong> {formData.rule_name}</div>
                    <div><strong>Symbol:</strong> {symbol} ({exchange})</div>
                    <div><strong>Scope:</strong> {formData.rule_scope === 'all_holdings' ? 'All Holdings' : 'Specific Account'}</div>
                    <div><strong>Action:</strong> {formData.action_type === 'SELL' ? 'Sell Order' : 'Alert Only'}</div>
                    <div>
                      <strong>Trigger:</strong> {formData.trigger_type === 'TRAILING_STOP_LOSS' ? 'Trailing Stop Loss' : 'Fixed Stop Loss'} 
                      {' '}at {formData.trigger_value}{formData.trigger_type === 'TRAILING_STOP_LOSS' ? '%' : '₹'}
                    </div>
                    {formData.action_type === 'SELL' && (
                      <>
                        <div>
                          <strong>Quantity:</strong> {
                            formData.sell_quantity_type === 'ALL_REMAINING' ? 'All Remaining' :
                            formData.sell_quantity_type === 'PERCENTAGE' ? `${formData.sell_quantity_value}%` :
                            `${formData.sell_quantity_value} shares`
                          }
                        </div>
                        <div><strong>Order Type:</strong> {formData.order_type}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={currentStep === 4 ? handleSubmit : handleNext}
              disabled={!canProceed() || isSubmitting}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                canProceed() && !isSubmitting
                  ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <LoadingSpinner />
              ) : currentStep === 4 ? (
                'Create Rule'
              ) : (
                'Next'
              )}
            </button>
            
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Previous
              </button>
            )}
            
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleCreationModal; 